import React, { useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import '../chartSetup'
import OverviewCard from './OverviewCard'
import ModalContainer from '../../../common/modal/ModalContainer'

const MAX_VISIBLE_ITEMS = 10

// Single legend row: color swatch + label on the left, percentage on the right.
const LegendRow = ({ item }) => (
    <li className='flex items-center justify-between gap-3'>
        <span className='flex items-center gap-2 min-w-0'>
            <span
                className='inline-block w-2.5 h-2.5 shrink-0'
                style={{ backgroundColor: item.color }}
            />
            <span className='regular-12 text-[#4d5055] truncate'>
                {item.label}
            </span>
        </span>
        <span className='regular-12 text-[#21242b] shrink-0'>
            {item.count != null ? (
                <>
                    <span className='weight-500'>
                        {Number(item.count).toLocaleString('en-US')}
                    </span>{' '}
                    <span className='text-[#6a6c71]'>({item.value}%)</span>
                </>
            ) : (
                <span className='weight-500'>{item.value}%</span>
            )}
        </span>
    </li>
)

// Donut chart with a label/percentage legend list beside it.
// `items` is an array of { label, value, color }.
// When there are more than MAX_VISIBLE_ITEMS categories, only the top ones are
// shown inline and the rest are viewable through a modal so the card layout
// stays intact.
const LegendDonut = ({
    title,
    subtitle,
    items,
    hideTooltipLabel = false,
    chartSize = 150
}) => {
    const [showAll, setShowAll] = useState(false)

    const data = {
        labels: items.map((item) => item.label),
        datasets: [
            {
                data: items.map((item) => item.value),
                backgroundColor: items.map((item) => item.color),
                borderWidth: 0,
                cutout: '70%'
            }
        ]
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) =>
                        hideTooltipLabel
                            ? ` ${ctx.parsed}%`
                            : `${ctx.label}: ${ctx.parsed}%`
                }
            }
        }
    }

    const hasOverflow = items.length > MAX_VISIBLE_ITEMS
    const visibleItems = hasOverflow ? items.slice(0, MAX_VISIBLE_ITEMS) : items

    return (
        <OverviewCard title={title} subtitle={subtitle} className='h-full'>
            <div className='h-full flex items-center gap-10'>
                <div
                    className='relative shrink-0'
                    style={{ width: chartSize, height: chartSize }}
                >
                    <Doughnut data={data} options={options} />
                </div>
                <div className='flex-1 min-w-0 flex flex-col gap-2'>
                    <ul className='flex flex-col gap-2'>
                        {visibleItems.map((item) => (
                            <LegendRow key={item.label} item={item} />
                        ))}
                    </ul>
                    {hasOverflow && (
                        <button
                            type='button'
                            onClick={() => setShowAll(true)}
                            className='self-start regular-12 weight-500 text-[#1466ff] hover:underline mt-1'
                        >
                            View all {items.length}
                        </button>
                    )}
                </div>
            </div>

            {showAll && (
                <ModalContainer
                    onCloseModal={() => setShowAll(false)}
                    defaultProps={{ size: 'lg', scrollable: true }}
                >
                    <div className='p-5'>
                        <div className='flex items-start justify-between gap-2 mb-4'>
                            <div>
                                <h4 className='regular-14 weight-500 text-[#21242b]'>
                                    {title}
                                </h4>
                                {subtitle && (
                                    <p className='regular-12 text-[#999a9d] mt-0.5'>
                                        {subtitle}
                                    </p>
                                )}
                            </div>
                            <button
                                type='button'
                                onClick={() => setShowAll(false)}
                                className='regular-20 text-[#999a9d] leading-none'
                                aria-label='Close'
                            >
                                &times;
                            </button>
                        </div>
                        <ul className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-h-[60vh] overflow-y-auto pr-1'>
                            {items.map((item) => (
                                <LegendRow key={item.label} item={item} />
                            ))}
                        </ul>
                    </div>
                </ModalContainer>
            )}
        </OverviewCard>
    )
}

export default LegendDonut
